/**
* @prop --uic-menu-bg-color-hover: default background color when hovered
* @prop --uic-menu-text-color: default text and icon color
* @prop --uic-menu-text-color-active: text and icon color when the item is active
* @prop --uic-menu-text-color-focus: text and icon color when the item has focus
* @prop --uic-menu-text-color-selected: text and icon color when the item is selected
* @prop --uic-menu-text-color-disabled: text and icon color when the item is disabled
* @prop --uic-menu-border-color: outline color
* @prop --uic-menu-border-color-focus: outline color when the item has focus
* @prop --uic-menu-border-style: outline style
* @prop --uic-menu-border-width: outline thickness
* @prop --uic-menu-indicator-color: color of the selected indicator
* @prop --uic-menu-indicator-transition: transition of the selected indicator
* @prop --uic-menu-item-color-level-0: background color for level 0 items
* @prop --uic-menu-item-color-level-0-hover: hover background color for level 0 items
* @prop --uic-menu-item-color-level-1: background color for level 1 items
* @prop --uic-menu-item-color-level-1-hover: hover background color for level 1 items
* @prop --uic-menu-item-color-level-2: background color for level 2 items
* @prop --uic-menu-item-color-level-2-hover: hover background color for level 2 items
* @prop --uic-menu-separator-color: color of the separator line
* @prop --uic-menu-separator-offset-horizontal: left/right offset of the separator line (vertical menu)
* @prop --uic-menu-separator-offset-vertical: top/bottom offset of the separator line (horizontal menu)
* @prop --uic-menu-separator-color: color of the separator line
* @prop --uic-menu-separator-thickness: thickness of the separator line
*/
.uic-menu-item {
  color: var(--uic-menu-text-color);
  display: block;
  flex: 0 0 auto;
  font-size: var(--uic-font-size-normal);
  font-weight: var(--uic-font-weight-bold);
  height: auto;
  line-height: var(--uic-line-height-normal);
  min-height: var(--uic-menu-height);
  position: relative;
  user-select: none;

  uic-badge {
    position: absolute;
    right: 15px;
    top: 8px;
    transform: translateX(50%);
  }

  .uic-menu.uic-menu--horizontal & {
    &[level="0"] > .uic-menu-item__dropdown {
      left: 0;
      max-height: calc(100vh - var(--uic-menu-y-offset));
      min-width: 100%;
      overflow-y: auto;
      position: absolute;
      top: var(--uic-menu-y-offset);
      width: max-content;
    }
  }

  /* stylelint-disable scss/selector-no-redundant-nesting-selector */
  .uic-menu-item__icon {
    flex: 0 0 auto;

    &.uic-menu-item__icon--expand,
    & + .uic-menu-item__label {
      margin-left: 8px;
    }
  }

  /* stylelint-enable scss/selector-no-redundant-nesting-selector */

  &:not([disabled]) {
    cursor: pointer;
  }

  &[disabled] {
    color: var(--uic-menu-text-color-disabled);
    pointer-events: none;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible > .uic-menu-item__item {
    color: var(--uic-menu-text-color-focus);
    outline: 2px solid var(--uic-menu-text-color-focus);
    outline-offset: -2px;
  }

  &[selected] > .uic-menu-item__item {
    color: var(--uic-menu-text-color-selected);

    &::after {
      background: var(--uic-menu-text-color-selected);
      bottom: 0;
      content: "";
      display: block;
      height: 4px;
      left: 0;
      position: absolute;
      width: 100%;
    }
  }

  .uic-menu-item__item {
    align-items: center;
    background: var(--uic-menu-item-color-level-2);
    box-sizing: border-box;
    color: inherit;
    display: flex;
    flex: 0 0 auto;
    height: auto;
    justify-content: space-between;
    min-height: var(--uic-menu-height);
    min-width: var(--uic-menu-height);
    padding: 0 15px;
    position: relative;
    text-decoration: none;

    &:hover {
      background: var(--uic-menu-item-color-level-2-hover);
    }

    &:active {
      color: var(--uic-menu-text-color-focus);
    }

    .uic-menu--vertical &,
    .uic-menu-item__submenu & {
      flex: 1 0 auto;
      justify-content: stretch;
    }

    .uic-menu-item__label {
      display: inline-block;
      flex: 1 0 auto;
      position: relative;
      white-space: nowrap;
    }

    .uic-menu-item__label.uic-menu-item__label--child-selected {
      font-weight: var(--uic-font-weight-normal);
      transform: translate(0, -10px);

      @media #{$breakpoint_tablet_small} {
        transform: translate(0, -8px);
      }
    }

    .uic-menu-item__label-selected-child {
      color: var(--uic-menu-text-color-hover);
      display: block;
      font-size: var(--uic-font-size-normal);
      font-weight: var(--uic-font-weight-bold);
      line-height: var(--uic-line-height-normal);
      overflow: hidden;
      position: absolute;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }
  }

  &.uic-menu-item--separator::after {
    background-color: var(--uic-menu-separator-color);
    content: "";
    display: block;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
  }

  .uic-menu--horizontal & {
    &.uic-menu-item--separator {
      margin-left: var(--uic-menu-separator-thickness);
      margin-top: 0;
    }

    &.uic-menu-item--separator::after {
      bottom: var(--uic-menu-separator-offset-vertical);
      height: auto;
      left: calc(-1 * var(--uic-menu-separator-thickness));
      right: auto;
      top: var(--uic-menu-separator-offset-vertical);
      width: var(--uic-menu-separator-thickness);
    }
  }

  .uic-menu--vertical &,
  .uic-menu-item__dropdown & {
    &.uic-menu-item--separator {
      margin-left: 0;
      margin-top: var(--uic-menu-separator-thickness);
    }

    &.uic-menu-item--separator::after {
      bottom: auto;
      height: var(--uic-menu-separator-thickness);
      left: var(--uic-menu-separator-offset-horizontal);
      right: var(--uic-menu-separator-offset-horizontal);
      top: calc(-1 * var(--uic-menu-separator-thickness));
      width: auto;
    }
  }

  &[level="0"] {
    > .uic-menu-item__item {
      background: var(--uic-menu-item-color-level-0);

      &:hover {
        background: var(--uic-menu-item-color-level-0-hover);
      }
    }
  }

  &[level="1"] {
    > .uic-menu-item__item {
      background: var(--uic-menu-item-color-level-1);

      &:hover {
        background: var(--uic-menu-item-color-level-1-hover);
      }
    }
  }

  &[level="3"] .uic-menu-item__label {
    padding-left: 15px;
  }

  .uic-menu-item__inner {
    align-items: center;
    display: flex;

    .uic-menu--vertical &,
    .uic-menu-item__submenu & {
      flex: 1 0 auto;
    }
  }

  .uic-menu-item__dropdown {
    display: none;
    position: relative;
  }

  &--expanded {
    > .uic-menu-item__item {
      .uic-menu-item__icon--expand {
        transform: rotate(180deg);
      }
    }

    > .uic-menu-item__dropdown {
      display: block;
    }
  }

  &--icon-only {
    > .uic-menu-item__item {
      justify-content: center;
    }
  }
}
